import { useMemo, useState } from 'react';

import { explainMovement, formatPeriodLong, MOVEMENT_TONE, summariseReview } from './review';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { formatDelta, MOVEMENT_LABEL } from '@/lib/api/review';
import type { BatchReview, StudentMovementRow } from '@/lib/api/review';

/**
 * One shared grid for every movement row: name, delta, badge, score.
 *
 * Fixed trailing tracks so the score column — the one the eye runs down — sits
 * at exactly the same x on every row, whatever the width of the badge or the
 * sign of the delta.
 */
const MOVEMENT_ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_2.5rem_5.5rem_2rem] items-center gap-3';

/** Rows shown before the reader asks for the rest. */
const PREVIEW_ROWS = 6;

export interface ReviewCyclePanelProps {
  review?: BatchReview | null;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** Navigate to a student's detail page. */
  onSelectStudent?: (userId: string) => void;
  className?: string;
}

/**
 * A batch's review cycle: what changed since the previous weekly reading.
 *
 * The panel is built around the same three-level idea the readiness card
 * settled on, because the scaling problem is identical — a batch is 26
 * students today and 300 next year:
 *
 *   1. a fixed-size sentence and chip row that read the same at any size,
 *   2. the few rows that need acting on, worst-first,
 *   3. "show all N" for the rest.
 *
 * Ordering is the server's, not this component's. The rows arrive ranked by
 * what to act on first, and re-sorting them here would put the explanation and
 * the order out of step.
 */
export function ReviewCyclePanel({
  review,
  loading = false,
  error = false,
  onRetry,
  onSelectStudent,
  className,
}: ReviewCyclePanelProps) {
  const [expanded, setExpanded] = useState(false);

  // Memoised on the payload rather than on a `?? []` fallback: the fallback
  // allocates a fresh array every render, which would make the memo a no-op.
  const rows = useMemo(() => review?.movements ?? [], [review?.movements]);
  const visible = useMemo(
    () => (expanded ? rows : rows.slice(0, PREVIEW_ROWS)),
    [rows, expanded],
  );

  if (loading) {
    return (
      <section className={cn('space-y-3', className)}>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-64" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={cn('rounded-md border border-border p-3', className)}>
        <p className="text-[12px] text-muted-foreground">This review could not be loaded.</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1.5 text-[11.5px] font-semibold text-primary hover:underline"
          >
            Try again
          </button>
        )}
      </section>
    );
  }

  if (!review) return null;

  return (
    <section className={className}>
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-[13px] font-semibold text-foreground">This week&apos;s review</h3>
        {review.period_start && (
          <p className="text-[10.5px] text-muted-foreground">
            Week of {formatPeriodLong(review.period_start)}
            {review.previous_period_start
              ? ` · compared with ${formatPeriodLong(review.previous_period_start)}`
              : ''}
          </p>
        )}
      </header>

      {/*
        The prose summary and the count chips said exactly the same thing, one
        under the other. Kept only while collecting, when there are no chips to
        replace it.
      */}
      {review.collecting && (
        <p className="mt-1 text-[12px] font-medium text-foreground">
          {summariseReview(review.counts, review.collecting)}
        </p>
      )}

      {review.collecting ? (
        <p className="mt-2 rounded-md border border-dashed border-border bg-muted/40 px-3 py-3 text-[11.5px] leading-relaxed text-muted-foreground">
          A reading is taken every week. Once there are two, this panel shows who moved and
          which way. Until then there is nothing to compare against — which is not the same as
          nothing having changed.
        </p>
      ) : (
        <>
          <CountChips review={review} />

          {rows.length === 0 ? (
            <p className="mt-2 text-[11.5px] text-muted-foreground">
              No students in this period.
            </p>
          ) : (
            <>
              <ul className="mt-2 divide-y divide-border/70">
                {visible.map((row) => (
                  <MovementRow
                    key={row.user_id}
                    row={row}
                    thresholds={review.thresholds}
                    onSelect={onSelectStudent}
                  />
                ))}
              </ul>

              {rows.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-2 text-[11.5px] font-semibold text-primary hover:underline"
                >
                  {expanded
                    ? `Show fewer`
                    : `View all ${rows.length} students`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The fixed-size shape of the period. Only non-zero buckets are rendered: a
 * row of zeroes is noise, and "0 newly at risk" is not information a placement
 * officer needs shown to them every week.
 */
function CountChips({ review }: { review: BatchReview }) {
  const { counts, thresholds } = review;
  const chips: Array<{ key: string; label: string; tone: string; title?: string }> = [];

  if (counts.newly_at_risk > 0) {
    chips.push({
      key: 'risk',
      label: `${counts.newly_at_risk} newly at risk`,
      tone: 'text-rose-700 bg-rose-50 border-rose-200',
      title: 'Dropped into the bottom tier this period',
    });
  }
  if (counts.slipped > 0) {
    chips.push({
      key: 'slipped',
      label: `${counts.slipped} slipped`,
      tone: MOVEMENT_TONE.slipped,
      title: `Lost at least ${thresholds.score_delta} points, or dropped a tier`,
    });
  }
  if (counts.improved > 0) {
    chips.push({
      key: 'improved',
      label: `${counts.improved} improved`,
      tone: MOVEMENT_TONE.improved,
      title: `Gained at least ${thresholds.score_delta} points, or gained a tier`,
    });
  }
  if (counts.flat > 0) {
    chips.push({
      key: 'flat',
      label: `${counts.flat} unchanged`,
      tone: MOVEMENT_TONE.flat,
      title: `Moved less than ${thresholds.score_delta} points`,
    });
  }
  if (counts.new > 0) {
    chips.push({ key: 'new', label: `${counts.new} new`, tone: MOVEMENT_TONE.new });
  }
  if (counts.stalled > 0) {
    chips.push({
      key: 'stalled',
      label: `${counts.stalled} inactive`,
      tone: 'text-amber-700 bg-amber-50 border-amber-200',
      title: `No submission or completed topic for ${thresholds.stale_days}+ days`,
    });
  }
  if (counts.low_confidence > 0) {
    chips.push({
      key: 'lowconf',
      label: `${counts.low_confidence} too early to judge`,
      tone: 'text-slate-600 bg-slate-50 border-slate-200',
      title: `Less than ${Math.round(thresholds.min_coverage * 100)}% of the formula has data`,
    });
  }

  if (chips.length === 0) return null;

  return (
    <ul className="mt-2 flex flex-wrap gap-1">
      {chips.map((chip) => (
        <li
          key={chip.key}
          title={chip.title}
          className={cn(
            'rounded-full border px-1.5 py-px text-[10.5px] font-semibold',
            chip.tone,
          )}
        >
          {chip.label}
        </li>
      ))}
    </ul>
  );
}

/**
 * One student's row. The score and its change lead, because that is what the
 * reader came for; the explanation follows in muted text so the row justifies
 * its own position in the list without competing with the number.
 */
function MovementRow({
  row,
  thresholds,
  onSelect,
}: {
  row: StudentMovementRow;
  thresholds: BatchReview['thresholds'];
  onSelect?: (userId: string) => void;
}) {
  const delta = formatDelta(row.delta);
  const interactive = !!onSelect;

  const content = (
    <>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold text-foreground">{row.name}</span>
          {row.newly_at_risk && (
            <span className="shrink-0 rounded-full border border-rose-200 bg-rose-50 px-1.5 py-px text-[10px] font-bold text-rose-700">
              At risk
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-[11px] leading-tight text-muted-foreground">
          {explainMovement(row, thresholds)}
        </p>
      </div>

      {/*
        Each of the three trailing values gets its own fixed track rather than
        sitting in a flex run. A "-14" next to a wide "No change" badge pushed
        the score left while a "±0" next to a narrow "Slipped" did not, so the
        rightmost column — the one the eye scans down — wandered by a few pixels
        every row.
      */}
      <span
        className={cn(
          'text-right text-[12px] font-bold tabular-nums',
          row.delta === undefined || row.delta === 0
            ? 'text-muted-foreground'
            : row.delta > 0
              ? 'text-emerald-700'
              : 'text-rose-700',
        )}
      >
        {delta ?? ''}
      </span>
      <span
        className={cn(
          'justify-self-center rounded-full border px-2 py-px text-center text-[10px] font-semibold',
          MOVEMENT_TONE[row.movement],
        )}
      >
        {MOVEMENT_LABEL[row.movement]}
      </span>
      <span className="text-right text-[13px] font-bold tabular-nums text-foreground">
        {row.score}
      </span>
    </>
  );

  return (
    <li>
      {interactive ? (
        <button
          type="button"
          onClick={() => onSelect?.(row.user_id)}
          className={cn(
            MOVEMENT_ROW_GRID,
            // Negative inset so the hover band extends past the text to the
            // card's padding, instead of stopping short and looking clipped.
            'w-full rounded-md px-2 py-2 text-left transition-colors -mx-2 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {content}
        </button>
      ) : (
        <div className={cn(MOVEMENT_ROW_GRID, 'px-2 py-2')}>{content}</div>
      )}
    </li>
  );
}
